import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { TrendingUp } from 'lucide-react';
import { getUTMParams } from '../utils/utm';
import i18n from '../i18n/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuickContactForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const getPrivacyPolicyUrl = () => {
    const urls = {
      'ru': 'https://estate-barcelona.com/terms-of-use',
      'en': 'https://estate-barcelona.com/en/terms-of-use',
      'es': 'https://estate-barcelona.com/es/terms-of-use'
    };
    return urls[i18n.language] || urls['ru'];
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const utmParams = getUTMParams();
      const response = await axios.post(`${BACKEND_URL}/api/contacts`, {
        ...formData,
        message: t('quickContact.title'),
        ...utmParams,
        language: i18n.language
      });
      
      if (response.data.success) {
        toast.success(t('quickContact.successMessage'));
        setFormData({ name: '', phone: '', email: '' });
        
        // Track Lead event in Meta Pixel
        if (window.fbq) {
          window.fbq('track', 'Lead');
        }
      }
    } catch (error) {
      toast.error(t('quickContact.errorMessage'));
      console.error('Quick contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 bg-gradient-to-br from-blue-50 via-white to-yellow-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-2">
              {t('quickContact.title')}
            </h2>
            <p className="text-lg md:text-xl text-gray-700">
              {t('quickContact.subtitle')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={`${t('quickContact.name')} *`}
                className="w-full"
              />
              
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder={`${t('quickContact.phone')} *`}
                className="w-full"
              />
              
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder={`${t('quickContact.email')} *`}
                className="w-full"
              />
              
              <Button
                type="submit"
                disabled={isSubmitting || !privacyAccepted}
                className="w-full bg-[#FFC400] hover:bg-[#DAA520] text-[#003366] text-lg font-bold"
              >
                {isSubmitting ? `${t('quickContact.submitButton')}...` : t('quickContact.submitButton')}
              </Button>
            </div>
            
            {/* Privacy Policy Checkbox */}
            <div className="flex items-start gap-2 justify-center">
              <input
                type="checkbox"
                id="privacy-quick"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                required
                className="mt-1 w-4 h-4 text-[#0052CC] border-gray-300 rounded focus:ring-[#0052CC]"
              />
              <label htmlFor="privacy-quick" className="text-sm text-gray-600">
                {t('quickContact.privacyPolicy')}{' '}
                <a 
                  href={getPrivacyPolicyUrl()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#0052CC] hover:underline"
                >
                  {t('quickContact.privacyPolicyLink')}
                </a>
              </label>
            </div>
          </form>

          {/* Sales Progress */}
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-[#FFC400]" />
              <p className="text-sm font-semibold text-gray-700">35% {t('quickContact.soldProgress')}</p>
            </div>
            
            {/* Progress Bar */}
            <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#FFC400] to-yellow-300 rounded-full transition-all duration-1000"
                style={{ width: '35%' }}
              ></div>
            </div>
            
            <div className="flex justify-between text-xs mt-2 text-gray-500">
              <span>0%</span>
              <span className="font-bold text-[#FFC400]">35%</span>
              <span>100%</span>
            </div>
            
            <p className="text-xs text-center mt-3 text-gray-600">
              {t('quickContact.soldMessage')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickContactForm;
